import React, { useState, useEffect, useCallback } from 'react';
import {
  getUserWebhooks,
  createUserWebhook,
  updateUserWebhook,
  deleteUserWebhook,
} from '../services/api';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import type { LayoutProps } from '../types/layout';

export type AppType = 'discord' | 'slack' | 'teams' | 'custom';

export interface UserWebhookItem {
  _id: string;
  userId: string;
  appType: AppType;
  name?: string;
  url: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

const APP_TYPES: { id: AppType; label: string; icon: React.ReactNode; color: string }[] = [
  {
    id: 'discord',
    label: 'Discord',
    color: '#5865F2',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    ),
  },
  {
    id: 'slack',
    label: 'Slack',
    color: '#4A154B',
    icon: (
      <svg viewBox="0 0 24 24" width="24" height="24">
        <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.521 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.521-2.523 2.526 2.526 0 0 1 2.521-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
      </svg>
    ),
  },
  {
    id: 'teams',
    label: 'Microsoft Teams',
    color: '#6264A7',
    icon: <MaterialIcon icon="groups" size={24} />,
  },
  {
    id: 'custom',
    label: 'Custom',
    color: '#6b7280',
    icon: <MaterialIcon icon="link" size={24} />,
  },
];

function maskUrl(url: string): string {
  if (!url || url.length < 40) return url;
  return url.slice(0, 20) + '…' + url.slice(-16);
}

export default function Webhooks({ setIsAuthenticated, darkMode, toggleDarkMode }: LayoutProps) {
  const [list, setList] = useState<UserWebhookItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<UserWebhookItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserWebhookItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ appType: 'discord' as AppType, name: '', url: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUserWebhooks();
      setList(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load webhooks');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = (appType?: AppType) => {
    setForm({ appType: appType ?? 'discord', name: '', url: '', description: '' });
    setAddModalOpen(true);
  };

  const openEdit = (item: UserWebhookItem) => {
    setEditingItem(item);
    setForm({
      appType: item.appType,
      name: item.name || '',
      url: item.url || '',
      description: item.description || '',
    });
    setEditModalOpen(true);
  };

  const openDelete = (item: UserWebhookItem) => {
    setDeleteTarget(item);
    setDeleteModalOpen(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.url.trim()) {
      toast.error('URL is required');
      return;
    }
    setSaving(true);
    try {
      await createUserWebhook({
        appType: form.appType,
        name: form.name.trim() || undefined,
        url: form.url.trim(),
        description: form.description.trim() || undefined,
      });
      toast.success('Webhook added');
      setAddModalOpen(false);
      load();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(typeof msg === 'string' ? msg : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !form.url.trim()) return;
    setSaving(true);
    try {
      await updateUserWebhook(editingItem._id, {
        appType: form.appType,
        name: form.name.trim() || undefined,
        url: form.url.trim(),
        description: form.description.trim() || undefined,
      });
      toast.success('Webhook updated');
      setEditModalOpen(false);
      setEditingItem(null);
      load();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(typeof msg === 'string' ? msg : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUserWebhook(deleteTarget._id);
      toast.success('Webhook removed');
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      load();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err && (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(typeof msg === 'string' ? msg : 'Error deleting');
    }
  };

  const appByType = (appType: AppType) => APP_TYPES.find((a) => a.id === appType) || APP_TYPES[APP_TYPES.length - 1];

  const grouped = APP_TYPES.map((app) => ({
    app,
    items: list.filter((w) => w.appType === app.id),
  }));

  if (loading) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="webhooks-page">
        <header className="workflows-page__header webhooks-page__header">
          <div>
            <h1 className="workflows-page__title">
              <MaterialIcon icon="webhook" size={28} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Webhooks
            </h1>
            <p className="workflows-page__subtitle">
              Add webhook URLs from apps like Discord, Slack or Microsoft Teams. Each webhook is stored with your account.
            </p>
          </div>
        </header>

        <div className="webhooks-grid-wrap">
          {grouped.map(({ app, items }) => (
            <div key={app.id} className="feature-card feature-card--start is-visible webhooks-app-group">
              <div className="feature-card__glow" />
              <div className="feature-card__content">
                <div className="webhooks-group-head">
                  <div className="webhooks-group-head__brand">
                    <span
                      className="feature-card__icon-wrapper webhooks-app-icon-wrap"
                      style={{
                        background: `${app.color}20`,
                        borderColor: `${app.color}60`,
                        color: app.color,
                      }}
                    >
                      <span className="webhooks-app-icon" style={{ background: app.color }}>
                        {app.icon}
                      </span>
                    </span>
                    <span className="webhooks-app-name">{app.label}</span>
                  </div>
                  <button
                    type="button"
                    className="workflows-page__new-btn"
                    onClick={() => openAdd(app.id)}
                  >
                    <MaterialIcon icon="add" size={18} />
                    Add webhook
                  </button>
                </div>
                <div className="webhooks-cells">
                {items.length === 0 ? (
                  <div className="webhooks-cell webhooks-cell--empty">
                    <span className="muted">No webhooks yet</span>
                  </div>
                ) : (
                  items.map((item) => (
                    <div key={item._id} className="webhooks-cell">
                      <div className="webhooks-cell__main">
                        <div className="webhooks-cell__title">
                          {item.name || appByType(item.appType).label}
                        </div>
                        {item.description ? (
                          <p className="webhooks-cell__desc">{item.description}</p>
                        ) : null}
                        <code className="webhooks-cell__url" title={item.url}>
                          {maskUrl(item.url)}
                        </code>
                      </div>
                      <div className="webhooks-cell__actions">
                        <button type="button" className="workflows-page__new-btn webhooks-cell__btn" onClick={() => openEdit(item)} title="Edit">
                          <MaterialIcon icon="edit" size={18} />
                        </button>
                        <button
                          type="button"
                          className="workflows-page__new-btn webhooks-cell__btn"
                          onClick={() => openDelete(item)}
                          title="Remove"
                        >
                          <MaterialIcon icon="delete" size={18} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {addModalOpen && (
        <div className="modal-overlay" onClick={() => setAddModalOpen(false)}>
          <div className="modal-content webhooks-integration-modal pro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pro-modal__header">
              <h2 className="pro-modal__title">Add webhook – {appByType(form.appType).label}</h2>
              <button type="button" className="pro-modal__close" onClick={() => !saving && setAddModalOpen(false)} aria-label="Close">
                <MaterialIcon icon="close" size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="pro-modal__body">
                <p className="muted small" style={{ margin: 0 }}>Paste webhook URL for {appByType(form.appType).label}.</p>
                <label>
                  Name (optional)
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. My channel"
                  />
                </label>
                <label>
                  Webhook URL *
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://…"
                    required
                  />
                </label>
                <label>
                  Description (optional)
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="What I use this webhook for"
                    rows={2}
                  />
                </label>
              </div>
              <div className="pro-modal__footer">
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModalOpen && editingItem && (
        <div className="modal-overlay" onClick={() => { setEditModalOpen(false); setEditingItem(null); }}>
          <div className="modal-content webhooks-integration-modal pro-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pro-modal__header">
              <h2 className="pro-modal__title">Edit webhook</h2>
              <button type="button" className="pro-modal__close" onClick={() => !saving && setEditModalOpen(false)} aria-label="Close">
                <MaterialIcon icon="close" size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="pro-modal__body">
                <label>Application</label>
                <div className="webhooks-integration-type-grid">
                  {APP_TYPES.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={`webhooks-integration-type-btn ${form.appType === a.id ? 'active' : ''}`}
                      style={{ '--app-color': a.color } as React.CSSProperties}
                      onClick={() => setForm((f) => ({ ...f, appType: a.id }))}
                    >
                      <span className="webhooks-integration-type-btn__icon">{a.icon}</span>
                      <span className="webhooks-integration-type-btn__label">{a.label}</span>
                    </button>
                  ))}
                </div>
                <label>
                  Name (optional)
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. My channel"
                  />
                </label>
                <label>
                  Webhook URL *
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://…"
                    required
                  />
                </label>
                <label>
                  Description (optional)
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Popis"
                    rows={2}
                  />
                </label>
              </div>
              <div className="pro-modal__footer">
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModalOpen && deleteTarget && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}
          onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}
        >
          <div className="modal" style={{ background: '#252525', padding: 24, borderRadius: 8, minWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <h3>Naozaj zmazať webhook?</h3>
            <p style={{ marginTop: 8, color: '#aaa' }}>
              Webhook „{deleteTarget.name || appByType(deleteTarget.appType).label}“ bude natrvalo zmazaný. Túto akciu nemožno vrátiť späť.
            </p>
            <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setDeleteModalOpen(false); setDeleteTarget(null); }}>Zrušiť</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>Zmazať</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
